import { routes } from '../constants';

import type { NetworkState } from '../hooks';
import type { SsoUserGroup } from '../types';

const STATS_ROOT = routes.STATS_FR;
/** Routes that are not gated by either network (utility pages) */
const NEUTRAL_ROUTES: string[] = [
  routes.NO_ACCESS,
  routes.PAGE_NOT_FOUND,
  routes.LOGOUT,
];
/** Returns `true` when `path` is served by the public (stats) backend */
const isStatsRoute = (path: string): boolean => path.startsWith('/stats');

/**
 * Adjusts an already-resolved permission redirect (or the current pathname)
 * so the user is never sent to a page whose backend is unreachable.
 *
 * @param permissionRedirect - redirect produced by existing permission logic
 * @param pathname           - current location.pathname
 * @param network            - latest result of useNetworkState()
 * @param grantedGroups      - user SSO groups (needed for fallback decisions)
 *
 * @returns a route string to redirect to, or undefined to render the current page.
 */
export const resolveNetworkRedirect = (
  permissionRedirect: string | undefined,
  pathname: string,
  network: NetworkState,
  grantedGroups: SsoUserGroup[],
): string | undefined => {
  const { privateConnected, publicConnected, isChecking } = network;
  // While health checks are still in-flight, don't override anything.
  // The caller will show a Loader until both permission and network are ready.
  if (isChecking) {
    return permissionRedirect;
  }
  // If both networks are up, permission logic is the single source of truth.
  if (privateConnected && publicConnected) {
    return permissionRedirect;
  }
  // The target page is what permissions decided, or where the user already is.
  const target = permissionRedirect ?? pathname;
  // Neutral pages (403, 404, logout) don't require any backend.
  if (NEUTRAL_ROUTES.includes(target)) {
    return permissionRedirect;
  }
  const targetNeedsPrivate = !isStatsRoute(target);
  const targetNeedsPublic = isStatsRoute(target);
  // --- Both networks down ---
  if (!privateConnected && !publicConnected) {
    return routes.ERRORS;
  }
  // --- Only private is down (VPN issue) ---
  if (!privateConnected && publicConnected) {
    if (targetNeedsPrivate) {
      // Can't show the CC page, fall back to stats if user has access
      // Admin implicitly has access to all groups, including Statistics
      const hasStatsAccess =
        grantedGroups.includes('Statistics') || grantedGroups.includes('Admin');
      if (hasStatsAccess) {
        // Already on a stats page — no redirect needed
        return isStatsRoute(pathname) ? undefined : STATS_ROOT;
      }
      // No stats permission, nothing we can show
      return routes.NO_ACCESS;
    }
    // Target is a stats page and public is fine, no override needed
    return permissionRedirect;
  }
  // --- Only public is down ---
  if (privateConnected && !publicConnected) {
    if (targetNeedsPublic) {
      // Can't show the stats page, fall back to CC if user has access
      const hasCCAccess =
        grantedGroups.includes('CommandCenter') ||
        grantedGroups.includes('Admin');
      if (hasCCAccess) {
        // Already on a CC (private) page — no redirect needed
        return !isStatsRoute(pathname) ? undefined : routes.MAIN_MAP;
      }
      // No CC permission, nothing we can show
      return routes.NO_ACCESS;
    }
    // Target is a CC page and private is fine, no override needed
    return permissionRedirect;
  }
  // Fallback (should never be reached)
  return permissionRedirect;
};
